import type { IngestedItem, MaterialType } from './dissolution-engine.js';

export interface TraitRequirement {
  materialType?: MaterialType;
  minCount?: number;
  essenceType?: string;
}

export interface StatModifiers {
  maxSpeedBonus: number;
  armorBonus: number;
  maxEnergyBonus: number;
  digestionSpeedBonus: number;
}

export interface TraitDefinition {
  id: string;
  name: string;
  description: string;
  tier: number;
  requirements: TraitRequirement;
  statModifiers: Partial<StatModifiers>;
}

export const SEED_TRAITS: readonly TraitDefinition[] = [
  {
    id: 'iron_membrane',
    name: 'Iron Membrane',
    description: 'Absorbed metallic minerals harden your outer gel layer, granting bonus physical resistance.',
    tier: 1,
    requirements: { materialType: 'mineral', minCount: 3 },
    statModifiers: { armorBonus: 15, maxSpeedBonus: -0.2 }
  },
  {
    id: 'chlorophyll_membrane',
    name: 'Chlorophyll Membrane',
    description: 'Symbiotic plant enzymes restore and expand internal gel energy capacity.',
    tier: 1,
    requirements: { materialType: 'organic', minCount: 3 },
    statModifiers: { maxEnergyBonus: 25, digestionSpeedBonus: 0.25 }
  },
  {
    id: 'mana_core_resonance',
    name: 'Mana Core Resonance',
    description: 'Attunement to concentrated essence allows faster metabolic dissolution.',
    tier: 2,
    requirements: { materialType: 'essence', minCount: 2 },
    statModifiers: { digestionSpeedBonus: 0.5, maxEnergyBonus: 40 }
  }
];

export class MutationSystem {
  private materialCounts: Record<MaterialType, number> = {
    organic: 0,
    mineral: 0,
    essence: 0,
    synthetic: 0
  };
  private essenceCounts = new Map<string, number>();
  private unlockedTraits = new Map<string, TraitDefinition>();

  public hasTrait(traitId: string): boolean {
    return this.unlockedTraits.has(traitId);
  }

  public getUnlockedTraits(): readonly TraitDefinition[] {
    return Array.from(this.unlockedTraits.values());
  }

  public unlockTrait(trait: TraitDefinition): void {
    this.unlockedTraits.set(trait.id, trait);
  }

  public recordAbsorption(item: IngestedItem): TraitDefinition[] {
    this.materialCounts[item.materialType] = (this.materialCounts[item.materialType] ?? 0) + 1;
    if (item.essenceType) {
      this.essenceCounts.set(item.essenceType, (this.essenceCounts.get(item.essenceType) ?? 0) + 1);
    }

    const newlyUnlocked: TraitDefinition[] = [];

    for (const trait of SEED_TRAITS) {
      if (this.unlockedTraits.has(trait.id)) continue;

      const req = trait.requirements;
      let meetsReq = true;

      if (req.materialType && req.minCount) {
        if ((this.materialCounts[req.materialType] ?? 0) < req.minCount) {
          meetsReq = false;
        }
      }

      if (req.essenceType && req.minCount) {
        if ((this.essenceCounts.get(req.essenceType) ?? 0) < req.minCount) {
          meetsReq = false;
        }
      }

      if (meetsReq) {
        this.unlockTrait(trait);
        newlyUnlocked.push(trait);
      }
    }

    return newlyUnlocked;
  }

  public getActiveModifiers(): StatModifiers {
    const total: StatModifiers = {
      maxSpeedBonus: 0,
      armorBonus: 0,
      maxEnergyBonus: 0,
      digestionSpeedBonus: 0
    };

    for (const trait of this.unlockedTraits.values()) {
      if (trait.statModifiers.maxSpeedBonus) total.maxSpeedBonus += trait.statModifiers.maxSpeedBonus;
      if (trait.statModifiers.armorBonus) total.armorBonus += trait.statModifiers.armorBonus;
      if (trait.statModifiers.maxEnergyBonus) total.maxEnergyBonus += trait.statModifiers.maxEnergyBonus;
      if (trait.statModifiers.digestionSpeedBonus) total.digestionSpeedBonus += trait.statModifiers.digestionSpeedBonus;
    }

    return total;
  }

  public getMaterialCounts(): Readonly<Record<MaterialType, number>> {
    return { ...this.materialCounts };
  }

  public setMaterialCounts(counts: Partial<Record<MaterialType, number>>): void {
    this.materialCounts = {
      ...this.materialCounts,
      ...counts
    };
  }
}
